import { ObjectBuilder } from 'components/WorkspaceEditor/factories'
import {
    ElementType,
    PartTypes,
} from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { useCallback } from 'react'
import { useShallowSelector } from '../../../Workspace2Store'
import { PromoteDemoteActions } from '../constants'
import { usePromoteDemoteSelector } from '../store/PromoteDemoteStore'
import { PromoteDemoteAction, PromoteDemoteVariables } from '../types'
import { findNodeById, findParentNode } from '../utils/nodeHelpers'
import {
    calculateDemoteVariables,
    calculatePromoteVariables,
} from '../utils/orderCalculators'

/**
 * Hook that provides the promote/demote service functionality
 * @returns Object containing the promoteDemote function
 */
export const usePromoteDemoteService = () => {
    const {
        contentItemToPromoteDemote,
        paragraphLevel,
    } = usePromoteDemoteSelector((s) => ({
        contentItemToPromoteDemote: s.contentItemToPromoteDemote,
        paragraphLevel: s.paragraphLevel,
    }))

    const {
        contentItemViewNodes,
        selectedProjectElementNode,
    } = useShallowSelector((s) => ({
        contentItemViewNodes: s.contentItemViewNodes,
        selectedProjectElementNode: s.selectedProjectElementNode,
    }))

    const buildInputVariables = useCallback(
        (action: PromoteDemoteAction): PromoteDemoteVariables | null => {
            if (
                !contentItemToPromoteDemote ||
                !contentItemToPromoteDemote?.id ||
                !contentItemViewNodes?.length
            ) {
                return null
            }

            // find current node to promote/demote
            const currentNode = findNodeById(
                contentItemViewNodes,
                contentItemToPromoteDemote.id
            )
            if (!currentNode) return null

            const currentParent = findParentNode(
                contentItemViewNodes,
                currentNode
            )

            // calculate new parent and order based on action type
            const { newParent, order } =
                action === PromoteDemoteActions.Promote
                    ? calculatePromoteVariables(
                          currentParent,
                          contentItemViewNodes,
                          paragraphLevel
                      )
                    : calculateDemoteVariables(
                          currentNode,
                          currentParent,
                          contentItemViewNodes
                      )

            // finally, build mutation variables
            const promoteDemonteContentInput = new ObjectBuilder<
                PromoteDemoteVariables['promoteDemonteContentInput']
            >()
                .set('contentId', currentNode.dtvItemId || '')
                .set(
                    'documentTemplateViewId',
                    currentNode.documentTemplateViewId || ''
                )
                .set('contentItemId', currentNode.id || '')
                .set('parentContentId', newParent?.dtvItemId)
                .set('parentContentItemId', newParent?.id)
                .set('productFamilyId', currentNode.productFamilyId || '')
                .set('order', order)
                .set(
                    'projectNuggetId',
                    selectedProjectElementNode?.elementType ===
                        ElementType.PRODUCTTYPE
                        ? selectedProjectElementNode.elementId ?? undefined
                        : undefined
                )
                .set(
                    'elementType',
                    !newParent &&
                        currentNode.partType === PartTypes.CONTENT_GROUP
                        ? ElementType.SUSTAINABILITY
                        : undefined
                )
                .set('topicContentItemsDetail', null)
                .build()

            return { promoteDemonteContentInput }
        },
        [
            contentItemToPromoteDemote,
            contentItemViewNodes,
            paragraphLevel,
            selectedProjectElementNode,
        ]
    )

    return { buildInputVariables }
}
